import { Button, toast, Typography } from "@suraasa/placebo-ui"
import { theme, useMediaQuery } from "@suraasa/placebo-ui-legacy"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { AttemptStatus } from "api/resources/assessments/types"
import { SUBMISSION_STATUS } from "api/resources/assignments/types"
import { ScheduleItem } from "api/resources/schedule/types"
import clsx from "clsx"
import { addDays, format } from "date-fns"
import CheckRestrictions from "features/AccessManagement/CheckRestrictions"
import {
  reloadOnAssessmentClose,
  startAssessment,
} from "features/Assessments/helpers"
import { buildAssignmentTitle } from "features/Assignments/utils"
import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import routes from "routes"
import { formatDate, getAssignmentSubmissionPageLink } from "utils/helpers"
import { trackElementOnPH } from "utils/tracking"

import calendarIcon from "../../../../src/assets/icons/calendar.svg"
import guidanceCalendarIcon from "../../../../src/assets/icons/guidance-calendar-icon.svg"

enum FilterType {
  all,
  classes,
  assignments,
  assessments,
}

const getBasicDetails = (task: ScheduleItem) => {
  switch (task.learningContentType) {
    case "assessment_schedule":
      return {
        title: task.assessment.title,
        startTime: task.startTime,
        endTime: task.endTime,
      }
    case "assignment":
      return {
        title: buildAssignmentTitle({
          title: task.title,
          shortTitle: task.shortTitle,
        }),
        endTime: task.targetDate,
      }
    case "class_schedule":
      return {
        title: task.classId.name,
        startTime: task.startTime,
        endTime: task.endTime,
      }
  }
}

const TaskTile = ({
  item,
  typeOfTask,
}: {
  item: ScheduleItem
  typeOfTask: FilterType
}) => {
  const details = getBasicDetails(item)

  const navigate = useNavigate()
  const formatDateTimeDisplay = (
    startTime?: string | null,
    endTime?: string | null
  ) => {
    const formattedDate = startTime
      ? format(new Date(startTime), "d MMMM")
      : endTime
      ? format(new Date(endTime), "d MMMM")
      : ""

    const formattedStartTime = startTime
      ? format(new Date(startTime), "h:mm a")
      : null
    const formattedEndTime = endTime
      ? format(new Date(endTime), "h:mm a")
      : null

    const timeRange = [formattedStartTime, formattedEndTime]
      .filter(Boolean)
      .join(" - ")

    return `${formattedDate}${timeRange ? `, ${timeRange}` : ""}`
  }

  const timeDisplay = formatDateTimeDisplay(
    details?.startTime,
    details?.endTime
  )

  const handleOpenAssessment = (item: ScheduleItem) => {
    if (
      item.learningContentType === "assessment_schedule" &&
      item.attempt !== null
    ) {
      if (
        item.attempt.status === AttemptStatus.NOT_STARTED ||
        item.attempt.status === AttemptStatus.IN_PROGRESS
      ) {
        // Missed
        if (new Date(item.endTime) < new Date()) {
          toast.info("You have missed this assessment")
          return
        }
        // Upcoming
        startAssessment({
          assessmentId: item.assessment.uuid,
          onClose: reloadOnAssessmentClose,
          userPlannerItemId: item.id.toString(),
        })
        return
      }
      if (item.attempt.status === AttemptStatus.COMPLETED) {
        navigate(
          routes.assessmentResult.replace(":attemptId", item.attempt.uuid),
          { state: { slug: item.planner.learningItem.slug } }
        )
        return
      }
    }
  }

  const getCTA = (task: ScheduleItem) => {
    switch (task.learningContentType) {
      case "assessment_schedule":
        handleOpenAssessment(task)
        return
      case "class_schedule":
        if (task.videoMeeting?.joiningUrl) {
          window.open(task.videoMeeting?.joiningUrl, "_blank")
        } else {
          toast.info("Class has not started yet!")
        }

        return
      case "assignment": {
        const submissionUuid = task.submissionUuid
        const submissionStatus = task.submissionStatus
        const courseSlug = task.planner.learningItem.slug
        const assignmentId = task.learningContentId.toString()

        if (!courseSlug) throw new Error("course slug not found")

        const link = getAssignmentSubmissionPageLink({
          submissionUuid,
          submissionStatus,
          courseSlug,
          assignmentId,
        })

        navigate(link)
        break
      }
    }
  }

  return (
    <CheckRestrictions
      accessStatus={item.accessStatus}
      render={({ isRestricted }) => (
        <button
          className="flex cursor-pointer flex-row gap-1.25"
          onClick={() => getCTA(item)}
          disabled={isRestricted}
        >
          <div
            className={clsx(
              "w-[3px] shrink-0 self-stretch rounded-full",
              (() => {
                if (typeOfTask === FilterType.all) return "bg-onSurface-600"
                if (typeOfTask === FilterType.classes) return "bg-[#9e5ce2]"
                if (typeOfTask === FilterType.assignments)
                  return "bg-decorative-three-100"
                if (typeOfTask === FilterType.assessments)
                  return "bg-decorative-one-100"
                return ""
              })()
            )}
          />

          <div className="flex min-w-0 flex-col items-start gap-0.5">
            <Typography
              variant={"smallBody"}
              className="text-left font-medium hover:underline"
            >
              {details.title}
            </Typography>
            <Typography
              variant={"smallBody"}
              className="font-medium text-onSurface-400"
            >
              {item.learningContentType === "assignment"
                ? `Due on ${timeDisplay}`
                : timeDisplay}
            </Typography>
          </div>
        </button>
      )}
    />
  )
}

const Filter = ({
  text,
  count,
  selected,
  onClick,
}: {
  text: FilterType
  count: number
  selected: boolean
  onClick: () => void
}) => {
  const hasCount = count !== 0

  const colorClasses = {
    [FilterType.all]: {
      bg: "bg-onSurface-600",
      text: "text-onSurface-600",
      border: "border-onSurface-600",
    },
    [FilterType.classes]: {
      bg: "bg-[#9e5ce2]",
      text: "text-[#9e5ce2]",
      border: "border-[#9e5ce2]",
    },
    [FilterType.assignments]: {
      bg: "bg-decorative-three-100",
      text: "text-decorative-three-100",
      border: "border-decorative-three-100",
    },
    [FilterType.assessments]: {
      bg: "bg-decorative-one-100",
      text: "text-decorative-one-100",
      border: "border-decorative-one-100",
    },
  }

  const currentColor = colorClasses[text]

  return (
    <button
      className={clsx("flex w-fit flex-row gap-0.5 rounded-lg border p-0.75", {
        [currentColor.border]: hasCount,
        "border-surface-200": !hasCount,
        [currentColor.bg]: selected && hasCount,
        "bg-surface-50": !selected || !hasCount,
      })}
      onClick={onClick}
    >
      <Typography
        variant="smallBody"
        className={clsx(
          "font-semibold capitalize",
          selected && hasCount
            ? "text-white"
            : hasCount
            ? currentColor.text
            : "text-onSurface-500"
        )}
      >
        {FilterType[text]}
      </Typography>

      <Typography
        variant="smallBody"
        className={clsx(
          selected && hasCount
            ? "text-white"
            : hasCount
            ? currentColor.text
            : "text-onSurface-500"
        )}
      >
        {count}
      </Typography>
    </button>
  )
}

const ScheduleBanner = () => {
  const date = new Date()
  const DATE_RANGE = 7

  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"))

  const filteredDataMaxCount = isSmDown ? 4 : 6

  const { data, isError, isSuccess } = useQuery({
    queryFn: () => {
      return api.schedule.getSchedule({
        params: {
          startDate: formatDate(date.toISOString(), "yyyy-MM-dd"),
          endDate: formatDate(
            addDays(date, DATE_RANGE - 1).toISOString(),
            "yyyy-MM-dd"
          ),
          loadPlannerItem: true,
          excludeAssignments: [
            SUBMISSION_STATUS.underReview,
            SUBMISSION_STATUS.notReviewed,
            SUBMISSION_STATUS.accepted,
          ],
        },
      })
    },
    queryKey: queries.schedule.upcoming(date.getDate()).queryKey,
  })

  const getFilterTypeFromLearningContentType = (
    learningContentType: string
  ): FilterType => {
    switch (learningContentType) {
      case "class_schedule":
        return FilterType.classes
      case "assignment":
        return FilterType.assignments
      case "assessment_schedule":
        return FilterType.assessments
      default:
        return FilterType.all
    }
  }

  const [selectedChip, setSelectedChip] = useState<FilterType>(FilterType.all)

  const isAllCountZero = (
    Object.keys(FilterType) as Array<keyof typeof FilterType>
  )
    .filter(key => isNaN(Number(key)))
    .every(key => {
      const filterType = FilterType[key]
      const count =
        data?.filter(
          item =>
            filterType === FilterType.all ||
            getFilterTypeFromLearningContentType(item.learningContentType) ===
              filterType
        ).length || 0

      return count === 0
    })

  return (
    <div className="mb-4.5 flex flex-col rounded-[16px] border border-surface-200 p-2.5">
      <div className="flex flex-row items-center justify-between">
        <Typography variant="title4">Schedule</Typography>
        <Link to={routes.mySchedule}>
          <div className="rounded-lg bg-[#0F131B08] p-1">
            <img src={calendarIcon} alt="" className="size-2" />
          </div>
        </Link>
      </div>
      {isAllCountZero ? null : (
        <div className="mb-1 mt-2 flex flex-wrap gap-1">
          {(Object.keys(FilterType) as Array<keyof typeof FilterType>)
            .filter(key => isNaN(Number(key)))
            .map(key => {
              const filterType = FilterType[key]
              const count =
                data?.filter(
                  item =>
                    filterType === FilterType.all ||
                    getFilterTypeFromLearningContentType(
                      item.learningContentType
                    ) === filterType
                ).length || 0

              return { key, filterType, count }
            })
            .sort((a, b) => {
              if (a.filterType === FilterType.all) return -1
              if (b.filterType === FilterType.all) return 1

              return b.count - a.count
            })
            .map(({ key, filterType, count }) =>
              count === 0 ? null : (
                <Filter
                  key={key}
                  text={filterType}
                  count={count}
                  selected={selectedChip === filterType}
                  onClick={
                    count !== 0 ? () => setSelectedChip(filterType) : () => {}
                  }
                />
              )
            )}
        </div>
      )}
      {isSuccess &&
        data &&
        (data.length === 0 ? (
          <div className="mx-2 mt-2 flex flex-wrap items-center justify-center gap-2.5 ">
            <div className="shrink-0 rounded-[24px] bg-onSurface-100 p-2">
              <img
                src={guidanceCalendarIcon}
                alt=""
                height={22}
                width={22}
              ></img>
            </div>
            <Typography
              variant={"strongSmallBody"}
              className="text-onSurface-400"
            >
              Nothing on your schedule yet!
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col">
            <div className="mt-1 h-full space-y-1.5">
              {(() => {
                const filteredData = data.filter(
                  item =>
                    selectedChip === FilterType.all ||
                    getFilterTypeFromLearningContentType(
                      item.learningContentType
                    ) === selectedChip
                )

                return (
                  <>
                    {filteredData.slice(0, filteredDataMaxCount).map(item => (
                      <TaskTile
                        key={item.id}
                        item={item}
                        typeOfTask={getFilterTypeFromLearningContentType(
                          item.learningContentType
                        )}
                      />
                    ))}
                    {filteredData.length > filteredDataMaxCount && (
                      <div className="mt-1.5 flex flex-row items-center gap-1">
                        <Typography
                          variant={"smallBody"}
                          className="text-onSurface-500"
                        >
                          + {filteredData.length - filteredDataMaxCount} more
                        </Typography>
                        <span className="size-1 rounded-full bg-onSurface-300" />
                        <Link to={routes.mySchedule}>
                          <Button
                            variant={"text"}
                            {...trackElementOnPH({
                              id: "view-full-calendar-from-sidebar",
                            })}
                          >
                            View Full Calendar
                          </Button>
                        </Link>
                      </div>
                    )}
                  </>
                )
              })()}
            </div>
          </div>
        ))}
      {isError && (
        <div className="py-8 text-center">
          Schedule not available. Please try again later.
        </div>
      )}
    </div>
  )
}

export default ScheduleBanner
